(() => {
  const copyResetTimers = new WeakMap();
  const featuredFilters = [
    'can',
    'product',
    'lifestyle',
    'ad',
    'mix pack',
    'lemon lime',
    'pineapple coconut',
    'fitness',
    'packaging',
  ];

  function downloadAsset(url, filename) {
    if (!url) return;
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'gigi-asset';
    link.target = '_blank';
    link.rel = 'noreferrer';
    link.click();
  }

  function copyText(text) {
    if (navigator.clipboard?.writeText && window.isSecureContext)
      return navigator.clipboard.writeText(text);
    return new Promise((resolve, reject) => {
      const field = document.createElement('textarea');
      field.value = text;
      field.readOnly = true;
      Object.assign(field.style, { position: 'fixed', opacity: '0' });
      document.body.appendChild(field);
      field.select();
      try {
        document.execCommand('copy') ? resolve() : reject(new Error('Copy command failed'));
      } catch (error) {
        reject(error);
      } finally {
        field.remove();
      }
    });
  }

  function showCopiedState(button, color) {
    const previousTimer = copyResetTimers.get(button);
    if (previousTimer) window.clearTimeout(previousTimer);
    button.classList.add('is-copied');
    button.setAttribute('aria-label', `Copied ${color} to clipboard`);
    copyResetTimers.set(
      button,
      window.setTimeout(() => {
        button.classList.remove('is-copied');
        button.setAttribute('aria-label', `Copy ${color} to clipboard`);
        copyResetTimers.delete(button);
      }, 3000)
    );
  }

  function normalizeSrcset(value) {
    const candidates = String(value || '')
      .split(',')
      .map((candidate) => candidate.trim())
      .filter(Boolean);
    if (!candidates.length) return '';

    for (const candidate of candidates) {
      const separator = candidate.lastIndexOf(' ');
      if (separator < 1 || !/^\d+w$/.test(candidate.slice(separator + 1))) return '';
      try {
        if (new URL(candidate.slice(0, separator), window.location.href).protocol !== 'https:')
          return '';
      } catch (_error) {
        return '';
      }
    }
    return candidates.join(', ');
  }

  function normalizeAsset(value) {
    if (!value || typeof value !== 'object') return null;
    const id = String(value.id || '').trim();
    const title = String(value.title || '').trim();
    const imageUrl = String(value.imageUrl || '').trim();
    const previewUrl = String(value.previewUrl || imageUrl).trim();
    const imageSrcset = normalizeSrcset(value.imageSrcset);
    const downloadUrl = String(value.downloadUrl || imageUrl).trim();
    const downloadName = String(value.downloadName || value.originalFilename || '').trim();
    const width = Number(value.width);
    const height = Number(value.height);
    const rawTags = Array.isArray(value.tags) ? value.tags : String(value.tags || '').split(',');
    const tags = [
      ...new Set(rawTags.map((tag) => String(tag).trim().toLowerCase()).filter(Boolean)),
    ];
    if (
      !id ||
      !title ||
      !imageUrl ||
      !downloadUrl ||
      !downloadName ||
      !Number.isFinite(width) ||
      !Number.isFinite(height) ||
      width < 1 ||
      height < 1
    )
      return null;
    try {
      if (
        new URL(imageUrl, window.location.href).protocol !== 'https:' ||
        new URL(previewUrl, window.location.href).protocol !== 'https:' ||
        new URL(downloadUrl, window.location.href).protocol !== 'https:'
      )
        return null;
    } catch (_error) {
      return null;
    }
    return {
      id,
      title,
      alt: String(value.alt || value.altText || title).trim() || title,
      tags,
      imageUrl,
      imageSrcset,
      previewUrl,
      downloadUrl,
      downloadName,
      width,
      height,
    };
  }

  function createCard(asset, iconTemplate) {
    const card = document.createElement('article');
    card.className = 'gigi-media-kit__asset-card';
    card.dataset.mediaKitCard = '';
    card.dataset.title = asset.title.toLowerCase();
    card.dataset.titleDisplay = asset.title;
    card.dataset.tags = asset.tags.join(',');
    card.dataset.url = asset.previewUrl;
    card.dataset.downloadUrl = asset.downloadUrl;
    card.dataset.downloadName = asset.downloadName;
    card.dataset.width = String(asset.width);
    card.dataset.height = String(asset.height);

    const image = document.createElement('img');
    image.src = asset.imageUrl;
    if (asset.imageSrcset) image.srcset = asset.imageSrcset;
    image.alt = asset.alt;
    image.width = asset.width;
    image.height = asset.height;
    image.loading = 'lazy';
    image.decoding = 'async';
    image.sizes =
      '(min-width: 1152px) 264px, (min-width: 990px) calc((100vw - 96px) / 4), (min-width: 750px) calc((100vw - 80px) / 3), (min-width: 481px) calc((100vw - 64px) / 2), calc((100vw - 48px) / 2)';

    const previewButton = document.createElement('button');
    previewButton.type = 'button';
    previewButton.className = 'gigi-media-kit__asset-preview';
    previewButton.dataset.mediaKitPreview = '';
    previewButton.setAttribute('aria-haspopup', 'dialog');
    previewButton.setAttribute('aria-label', `Preview ${asset.title}`);

    const overlay = document.createElement('div');
    overlay.className = 'gigi-media-kit__asset-overlay';
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'gigi-media-kit__asset-download';
    button.dataset.mediaKitDownload = '';
    button.setAttribute('aria-label', `Download ${asset.title}`);
    const icon = document.createElement('span');
    icon.setAttribute('aria-hidden', 'true');
    if (iconTemplate?.content) icon.appendChild(iconTemplate.content.cloneNode(true));
    button.appendChild(icon);
    overlay.appendChild(button);
    const clip = document.createElement('div');
    clip.className = 'gigi-media-kit__asset-clip';
    clip.append(image, previewButton, overlay);
    card.appendChild(clip);
    return card;
  }

  function setupMediaKit(root) {
    if (!root || root.dataset.gigiMediaKitReady === 'true') return;
    root.dataset.gigiMediaKitReady = 'true';
    const grid = root.querySelector('[data-media-kit-grid]');
    const filters = root.querySelector('[data-media-kit-filters]');
    const searchInput = root.querySelector('[data-media-kit-search]');
    const clearButton = root.querySelector('[data-media-kit-clear]');
    const emptyState = root.querySelector('[data-media-kit-empty]');
    const emptyQuery = root.querySelector('[data-media-kit-empty-query]');
    const status = root.querySelector('[data-media-kit-status]');
    const errorState = root.querySelector('[data-media-kit-error]');
    const retryButton = root.querySelector('[data-media-kit-retry]');
    const iconTemplate = root.querySelector('[data-media-kit-download-icon]');
    const modal =
      document.getElementById(root.dataset.mediaKitModalId || '') ||
      root.querySelector('[data-media-kit-modal]');
    const modalDownload = modal?.querySelector('[data-media-kit-modal-download]');
    let cards = [];
    let activeFilter = 'all';
    let activeController = null;
    let loadRequest = 0;
    let lastLoadedAt = 0;
    let refreshTimer = null;

    function updateAssets() {
      const query = (searchInput?.value || '').trim().toLowerCase();
      let visibleCount = 0;
      cards.forEach((card) => {
        const tags = (card.dataset.tags || '').split(',');
        const matchesQuery =
          !query ||
          (card.dataset.title || '').includes(query) ||
          tags.some((tag) => tag.includes(query));
        const matchesFilter = activeFilter === 'all' || tags.includes(activeFilter);
        card.hidden = !(matchesQuery && matchesFilter);
        if (!card.hidden) visibleCount += 1;
      });
      if (clearButton) clearButton.hidden = !query;
      if (emptyState) emptyState.hidden = cards.length === 0 || visibleCount !== 0;
      if (emptyQuery) emptyQuery.textContent = query;
    }

    function renderFilters(assets) {
      if (!filters) return;
      const presentTags = new Set(assets.flatMap((asset) => asset.tags));
      const tags = featuredFilters.filter((tag) => presentTags.has(tag));
      if (activeFilter !== 'all' && !tags.includes(activeFilter)) activeFilter = 'all';
      filters.textContent = '';
      ['all', ...tags].forEach((filter) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = filter;
        button.dataset.mediaKitFilter = filter;
        button.classList.toggle('is-selected', filter === activeFilter);
        button.setAttribute('aria-pressed', filter === activeFilter ? 'true' : 'false');
        filters.appendChild(button);
      });
      filters.hidden = assets.length === 0;
    }

    function openModal(card) {
      if (!modal || typeof modal.show !== 'function') return;
      card.dataset.gigiPageScrollY = String(window.scrollY || window.pageYOffset || 0);
      modal.show(card);
    }

    async function loadAssets() {
      if (!grid?.dataset.feedUrl) return;
      const requestId = ++loadRequest;
      activeController?.abort();
      grid.setAttribute('aria-busy', 'true');
      if (status && cards.length === 0) {
        status.classList.remove('is-empty');
        status.hidden = false;
        status.textContent = 'Loading media assets...';
      }
      if (errorState) errorState.hidden = true;
      if (retryButton) retryButton.disabled = true;
      const controller = typeof AbortController === 'function' ? new AbortController() : null;
      activeController = controller;
      const timeout = controller ? window.setTimeout(() => controller.abort(), 5000) : null;
      try {
        const response = await fetch(grid.dataset.feedUrl, {
          credentials: 'omit',
          headers: { Accept: 'application/json' },
          signal: controller?.signal,
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        if (requestId !== loadRequest) return;
        const list = data && Array.isArray(data.assets) ? data.assets : null;
        if (!list) throw new Error('Invalid media-kit response');
        const assets = list.map(normalizeAsset).filter(Boolean);
        if (
          assets.length !== list.length ||
          new Set(assets.map((asset) => asset.id)).size !== assets.length
        )
          throw new Error('Invalid media-kit asset');
        if (modal?.hasAttribute('open')) return;
        grid.textContent = '';
        assets.forEach((asset) => {
          grid.appendChild(createCard(asset, iconTemplate));
        });
        cards = Array.from(grid.querySelectorAll('[data-media-kit-card]'));
        renderFilters(assets);
        updateAssets();
        lastLoadedAt = Date.now();
        if (status) {
          status.classList.toggle('is-empty', assets.length === 0);
          status.hidden = assets.length !== 0;
          status.textContent = 'No media assets are currently available.';
        }
      } catch (error) {
        if (requestId !== loadRequest) return;
        console.warn('[media-kit] could not load media assets', error);
        if (cards.length === 0) {
          grid.textContent = '';
          if (filters) filters.hidden = true;
          if (status) {
            status.classList.remove('is-empty');
            status.hidden = true;
          }
          if (errorState) errorState.hidden = false;
        }
      } finally {
        if (timeout) window.clearTimeout(timeout);
        if (requestId === loadRequest) {
          grid.removeAttribute('aria-busy');
          if (retryButton) retryButton.disabled = false;
          activeController = null;
        }
      }
    }

    function refreshIfStale() {
      if (
        root.isConnected &&
        !activeController &&
        !modal?.hasAttribute('open') &&
        document.visibilityState === 'visible' &&
        Date.now() - lastLoadedAt >= 15000
      ) {
        loadAssets();
      }
    }

    function refreshAfterModalClose() {
      if (refreshTimer) window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(() => {
        refreshTimer = null;
        refreshIfStale();
      }, 0);
    }

    filters?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-media-kit-filter]');
      if (!button) return;
      activeFilter = button.dataset.mediaKitFilter || 'all';
      filters.querySelectorAll('[data-media-kit-filter]').forEach((item) => {
        const selected = item === button;
        item.classList.toggle('is-selected', selected);
        item.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
      updateAssets();
    });
    searchInput?.addEventListener('input', updateAssets);
    clearButton?.addEventListener('click', () => {
      searchInput.value = '';
      searchInput.focus();
      updateAssets();
    });
    retryButton?.addEventListener('click', loadAssets);
    window.addEventListener('focus', refreshIfStale);
    document.addEventListener('visibilitychange', refreshIfStale);
    document.body.addEventListener('modalClosed', refreshAfterModalClose);
    root.querySelectorAll('[data-media-kit-copy-color]').forEach((button) => {
      button.addEventListener('click', async () => {
        const color = button.dataset.mediaKitCopyColor || '';
        if (!color) return;
        try {
          await copyText(color);
          showCopiedState(button, color);
        } catch (error) {
          console.error('Unable to copy media kit color', error);
        }
      });
    });
    grid?.addEventListener('click', (event) => {
      const card = event.target.closest('[data-media-kit-card]');
      if (!card) return;
      if (event.target.closest('[data-media-kit-download]')) {
        event.preventDefault();
        event.stopPropagation();
        downloadAsset(
          card.dataset.downloadUrl || card.dataset.url || '',
          card.dataset.downloadName
        );
        return;
      }
      if (event.target.closest('[data-media-kit-preview]')) openModal(card);
    });
    grid?.addEventListener(
      'error',
      (event) => {
        const card = event.target.closest?.('[data-media-kit-card]');
        if (!card) return;
        const hadFocus = card.contains(document.activeElement);
        const unavailableTitle = card.dataset.titleDisplay || 'A media asset';
        card.remove();
        cards = cards.filter((candidate) => candidate !== card);
        renderFilters(
          cards.map((candidate) => ({
            tags: (candidate.dataset.tags || '').split(',').filter(Boolean),
          }))
        );
        updateAssets();
        if (status) {
          status.classList.remove('is-empty');
          status.hidden = false;
          status.textContent = `${unavailableTitle} is temporarily unavailable and was removed from this view.`;
        }
        if (hadFocus) {
          window.requestAnimationFrame(() => {
            const nextPreview = cards
              .find((candidate) => !candidate.hidden)
              ?.querySelector('[data-media-kit-preview]');
            const selectedFilter = !filters?.hidden
              ? filters?.querySelector('[aria-pressed="true"]')
              : null;
            const fallback = cards.length === 0 ? retryButton : selectedFilter || searchInput;
            (nextPreview || fallback)?.focus();
          });
        }
        if (cards.length === 0 && errorState) errorState.hidden = false;
      },
      true
    );
    modalDownload?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      const currentCard = cards.find(
        (card) => card.dataset.url === (modalDownload.dataset.mediaKitUrl || modalDownload.href)
      );
      downloadAsset(
        currentCard?.dataset.downloadUrl || modalDownload.dataset.mediaKitUrl || modalDownload.href,
        modalDownload.dataset.mediaKitFilename || modalDownload.download
      );
    });
    root.gigiMediaKitCleanup = () => {
      loadRequest += 1;
      activeController?.abort();
      if (refreshTimer) window.clearTimeout(refreshTimer);
      window.removeEventListener('focus', refreshIfStale);
      document.removeEventListener('visibilitychange', refreshIfStale);
      document.body.removeEventListener('modalClosed', refreshAfterModalClose);
    };
    loadAssets();
  }

  function setupAllMediaKits(scope = document) {
    scope.querySelectorAll('[data-gigi-media-kit]').forEach(setupMediaKit);
  }
  if (document.readyState === 'loading')
    document.addEventListener('DOMContentLoaded', () => setupAllMediaKits());
  else setupAllMediaKits();
  document.addEventListener('shopify:section:load', (event) => setupAllMediaKits(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.('[data-gigi-media-kit]')
      ? event.target
      : event.target.querySelector?.('[data-gigi-media-kit]');
    root?.gigiMediaKitCleanup?.();
  });
})();
